import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabDescriptor, OperationBCI } from '../types';
import { buildOperationTabs, computeDefaultTabIndex } from '../domain/operationTabs';

// Hook mince : la logique (visibilite/gating/onglet par defaut) vit dans
// domain/operationTabs. Ici, uniquement le cablage de l'onglet actif a l'URL (?tab=).
export const useOperationTabs = (operation: OperationBCI | null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const descriptors = useMemo<TabDescriptor[]>(
    () => (operation ? buildOperationTabs(operation) : []),
    [operation],
  );

  const defaultIndex = useMemo(
    () => (operation ? computeDefaultTabIndex(descriptors, operation) : 0),
    [descriptors, operation],
  );

  const tabParam = searchParams.get('tab');
  const activeIndex = useMemo(() => {
    if (tabParam !== null) {
      const parsed = parseInt(tabParam, 10);
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < descriptors.length) return parsed;
      // Fallback par kind (ex. ?tab=evaluation depuis un lien externe), comme cote MS.
      const byKind = descriptors.findIndex((d) => d.kind === tabParam);
      if (byKind >= 0) return byKind;
    }
    return defaultIndex;
  }, [tabParam, defaultIndex, descriptors]);

  // L'onglet actif est persiste dans l'URL : compatible avec les feuilles qui
  // rechargent la page, et avec la navigation depuis les listes.
  const setActiveIndex = useCallback(
    (index: number) => {
      const target = descriptors[index];
      if (!target || target.disabled || target.inactive) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', String(index));
      setSearchParams(next, { replace: true });
    },
    [descriptors, searchParams, setSearchParams],
  );

  return {
    descriptors,
    activeIndex,
    activeTab: descriptors[activeIndex] ?? null,
    setActiveIndex,
  };
};
